import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';
import stockReorderApi, { errorMessage, readBlobError } from '../../api/stockReorderApi';
import ReorderTab from './components/ReorderTab';
import HistoryTab from './components/HistoryTab';
import SupplierMappingTab from './components/SupplierMappingTab';
import SupplierRulesTab from './components/SupplierRulesTab';
import BranchesTab from './components/BranchesTab';
import AccessTab from './components/AccessTab';
import StockAvailabilityTab from './components/StockAvailabilityTab';
import StockUploadsTab from './components/StockUploadsTab';
import { recomputeSummary } from './reorderUtils';
import './StockReorder.css';

const BRANCH_KEY = 'stockReorder.branchId';

/**
 * Stock Reorder module.
 *
 * Two permission layers apply here and they answer different questions:
 *  - reception's card/actions decide whether this page opens at all, and
 *  - the Python service's per-user scope decides which branches are visible.
 * The scope is fetched once on mount; `isAdmin` from it unlocks the
 * administrative tabs.
 *
 * The generated sheet lives here rather than in the Reorder tab because three
 * tabs read it — reorder, suppliers, and a saved sheet opened from history —
 * and a manual override made in any of them has to be reflected in all of them.
 */
const StockReorder = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('reorder');
    const [scope, setScope] = useState(null);
    const [businessUnits, setBusinessUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [branchId, setBranchId] = useState(() => localStorage.getItem(BRANCH_KEY) || '');
    const [sheet, setSheet] = useState(null);
    const [liveSheet, setLiveSheet] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [busyPart, setBusyPart] = useState('');
    const [toasts, setToasts] = useState([]);
    const [actionError, setActionError] = useState('');
    const [draft, setDraft] = useState(null);
    const [draftBusy, setDraftBusy] = useState(false);
    // An older stock report opened from Stock Uploads; null = current stock.
    const [stockHistory, setStockHistory] = useState(null);

    const showToast = useCallback((message) => {
        const id = Date.now() + Math.random();
        setToasts((list) => [...list, { id, message }]);
        setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 4000);
    }, []);

    const loadBusinessUnits = useCallback(async () => {
        const data = await stockReorderApi.listBusinessUnits();
        setBusinessUnits(data.business_units || []);
        return data.business_units || [];
    }, []);

    /**
     * Look for a stashed sheet from a previous visit.
     *
     * Declared before the mount effect that calls it — a `useCallback` is a
     * plain `const`, so referencing it from an earlier dependency array is a
     * temporal-dead-zone crash, not a hoisted function call.
     */
    const refreshDraft = useCallback(async () => {
        try {
            const data = await stockReorderApi.getDraft();
            setDraft(data.draft || null);
        } catch (err) {
            // Not having a draft must never take the page down with it.
            setDraft(null);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [scopeData, units] = await Promise.all([
                stockReorderApi.myScope(),
                loadBusinessUnits()
            ]);
            setScope(scopeData);
            // A remembered branch the user has since lost access to would leave
            // every dropdown showing a blank they cannot explain.
            setBranchId((current) =>
                current && units.some((bu) => bu.id === current) ? current : units[0]?.id || ''
            );
        } catch (err) {
            setError(errorMessage(err, 'Could not reach the stock reorder service'));
        } finally {
            setLoading(false);
        }
    }, [loadBusinessUnits]);

    useEffect(() => {
        load();
        refreshDraft();
    }, [load, refreshDraft]);

    useEffect(() => {
        if (branchId) localStorage.setItem(BRANCH_KEY, branchId);
    }, [branchId]);

    const isAdmin = Boolean(scope?.is_admin);
    const viewingSnapshot = Boolean(snapshot);

    /**
     * Inline overrides are a PUT, which `requireCard` maps to the `edit`
     * action. Showing the controls to someone without it would only produce a
     * 403 once they touched a cell, so the cells stay read-only instead.
     */
    const canEdit =
        user?.role === 'system_admin' ||
        hasCardAction(user?.permissions || {}, 'stock-reorder', 'edit');
    const editable = canEdit && !viewingSnapshot;

    // Uploading a stock report is a POST → `create`. Hidden without it; the
    // server refuses it regardless.
    const canUploadStock =
        user?.role === 'system_admin' ||
        hasCardAction(user?.permissions || {}, 'stock-reorder', 'create');

    /** Switching branch invalidates whatever sheet is on screen. */
    const changeBranch = useCallback(
        (id) => {
            setBranchId(id);
            setSheet(null);
            setLiveSheet(null);
            setSnapshot(null);
            setStockHistory(null);
            const unit = businessUnits.find((bu) => bu.id === id);
            if (unit) showToast(`Switched to ${unit.name}.`);
        },
        [businessUnits, showToast]
    );

    /**
     * The draft banner.
     *
     * A generated sheet costs a full workbook parse, and it used to live only
     * in this component's state — a reload or a trip to another module threw it
     * away. Every generate now stashes one server-side; this offers it back.
     */

    const applySheet = useCallback(
        (next) => {
            setSheet(next);
            setLiveSheet(next);
            setSnapshot(null);
            // Generating replaced the stored draft — keep the banner in step.
            refreshDraft();
        },
        [refreshDraft]
    );

    const restoreDraft = useCallback(async () => {
        if (!draft) return;
        setDraftBusy(true);
        setActionError('');
        try {
            const data = await stockReorderApi.openDraft(draft.id);
            const restored = {
                items: data.items || [],
                summary: data.summary,
                branches: data.branches || [],
                params: data.params,
                isCombined: data.is_combined,
                businessUnitId: data.business_unit_id,
                // No request: the run that produced it is gone, so "save sheet"
                // has nothing to recompute from. Exports no longer need it —
                // `sheetId` sends them to /export-snapshot, which builds the
                // file from the stored sheet instead of re-running.
                request: null,
                sheetId: data.id || draft.id
            };
            setSheet(restored);
            setLiveSheet(restored);
            setSnapshot(null);
            if (data.business_unit_id) setBranchId(data.business_unit_id);
            showToast('Draft restored.');
        } catch (err) {
            setActionError(errorMessage(err, 'Could not open that draft'));
        } finally {
            setDraftBusy(false);
        }
    }, [draft, showToast]);

    const keepDraft = useCallback(async () => {
        if (!draft) return;
        const label = window.prompt('Name this saved order (optional):', '');
        if (label === null) return;
        setDraftBusy(true);
        setActionError('');
        try {
            await stockReorderApi.keepDraft(draft.id, label);
            setDraft(null);
            showToast('Kept — find it under Saved Sheets.');
        } catch (err) {
            setActionError(errorMessage(err, 'Could not keep that draft'));
        } finally {
            setDraftBusy(false);
        }
    }, [draft, showToast]);

    const discardDraft = useCallback(async () => {
        if (!draft) return;
        if (!window.confirm('Delete this draft permanently? It cannot be recovered.')) return;
        setDraftBusy(true);
        setActionError('');
        try {
            await stockReorderApi.discardDraft(draft.id);
            setDraft(null);
            showToast('Draft deleted.');
        } catch (err) {
            setActionError(errorMessage(err, 'Could not delete that draft'));
        } finally {
            setDraftBusy(false);
        }
    }, [draft, showToast]);

    const openSnapshot = useCallback(
        (data, buId) => {
            setSheet({
                items: data.items || [],
                summary: data.summary,
                branches: [],
                params: data.params,
                isCombined: false,
                businessUnitId: buId,
                request: null,
                // Same as a restored draft: exports come from the stored sheet.
                sheetId: data.id
            });
            setSnapshot({ id: data.id, label: data.label, created_at: data.created_at });
            setActiveTab('reorder');
            showToast('Opened saved sheet — read-only.');
        },
        [showToast]
    );

    const backToLive = useCallback(() => {
        setSnapshot(null);
        setSheet(liveSheet);
    }, [liveSheet]);

    /**
     * Write a manual override, then patch the item in place.
     *
     * Re-generating after every edit would cost a full workbook parse, so the
     * summary is recomputed locally the same way the server does it.
     */
    const editItem = useCallback(
        async (item, fields) => {
            const buId = item.business_unit_id || sheet?.businessUnitId || '';
            if (!buId) return;
            setBusyPart(item.part_number);
            setActionError('');
            try {
                await stockReorderApi.setReorderOverride({
                    businessUnitId: buId,
                    partNumber: item.part_number,
                    ...fields
                });
                const patch = (list) =>
                    list.map((row) => {
                        if (row.part_number !== item.part_number) return row;
                        const next = { ...row, manual_override: true };
                        if (fields.needsReorder !== undefined) next.needs_reorder = fields.needsReorder;
                        if (fields.reorderQty !== undefined) {
                            next.reorder_qty = fields.reorderQty;
                            next.reorder_value =
                                Math.round(fields.reorderQty * (row.cost || 0) * 100) / 100;
                        }
                        return next;
                    });
                setSheet((prev) => {
                    if (!prev) return prev;
                    const items = patch(prev.items);
                    return { ...prev, items, summary: recomputeSummary(items) };
                });
                setLiveSheet((prev) => {
                    if (!prev) return prev;
                    const items = patch(prev.items);
                    return { ...prev, items, summary: recomputeSummary(items) };
                });
                showToast(
                    fields.needsReorder !== undefined
                        ? `“${item.part_number}” reorder flag updated.`
                        : `“${item.part_number}” quantity updated.`
                );
            } catch (err) {
                setActionError(errorMessage(err, 'Could not save that change'));
            } finally {
                setBusyPart('');
            }
        },
        [sheet, showToast]
    );

    const resetItem = useCallback(
        async (item) => {
            const buId = item.business_unit_id || sheet?.businessUnitId || '';
            if (!buId) return;
            setBusyPart(item.part_number);
            setActionError('');
            try {
                await stockReorderApi.clearReorderOverride(item.part_number, buId);
                /**
                 * Put the calculated numbers back, not just the ✎.
                 *
                 * Clearing only the flag left the override's value on screen
                 * looking like a calculated one, and the real figure was
                 * reachable only by regenerating — impossible on a restored
                 * draft or a saved sheet, so undo was a dead end there.
                 */
                const clear = (list) =>
                    list.map((row) => {
                        if (row.part_number !== item.part_number) return row;
                        const next = { ...row, manual_override: false };
                        if (row.calc_needs_reorder !== undefined) {
                            next.needs_reorder = row.calc_needs_reorder;
                        }
                        if (row.calc_reorder_qty !== undefined) {
                            next.reorder_qty = row.calc_reorder_qty;
                            next.reorder_value =
                                row.calc_reorder_value !== undefined
                                    ? row.calc_reorder_value
                                    : Math.round(row.calc_reorder_qty * (row.cost || 0) * 100) / 100;
                        }
                        return next;
                    });
                const patched = (prev) => {
                    if (!prev) return prev;
                    const items = clear(prev.items);
                    return { ...prev, items, summary: recomputeSummary(items) };
                };
                setSheet(patched);
                setLiveSheet(patched);
                showToast(`“${item.part_number}” reset to the calculated value.`);
            } catch (err) {
                setActionError(errorMessage(err, 'Could not reset that item'));
            } finally {
                setBusyPart('');
            }
        },
        [sheet, showToast]
    );

    /**
     * A sheet can be exported by either of two routes, and which one applies
     * depends on where the sheet came from.
     *
     * A sheet just generated in this session still has its `request`, so the
     * server can re-run the calculation from the source workbooks — including
     * uploads, which only exist in memory.
     *
     * A restored draft or a saved sheet has no request; the run that produced
     * it is gone. It does have a `sheetId`, so /export-snapshot builds the file
     * from the stored items instead. This is the case that used to leave the
     * buttons doing nothing at all.
     */
    const singleOpts = useMemo(() => {
        const request = sheet?.request;
        if (!request) return null;
        return {
            params: request.params,
            businessUnitId: request.businessUnitId,
            files: request.files
        };
    }, [sheet]);

    const storedSheetId = sheet?.sheetId || null;
    const canExport = Boolean(singleOpts || storedSheetId);

    const runExport = useCallback(async (fn, ...args) => {
        setActionError('');
        try {
            await fn(...args);
        } catch (err) {
            setActionError(await readBlobError(err, 'Export failed'));
        }
    }, []);

    const exportSheet = useCallback(
        (kind) => {
            const request = sheet?.request;
            if (!request) {
                if (!storedSheetId) return;
                return runExport(
                    stockReorderApi.exportSnapshot,
                    storedSheetId,
                    kind === 'pdf' ? 'pdf' : 'xlsx'
                );
            }
            if (request.isCombined) {
                return runExport(
                    kind === 'pdf'
                        ? stockReorderApi.exportMultiPdf
                        : stockReorderApi.exportMultiExcel,
                    request.selectedIds,
                    request.params
                );
            }
            return runExport(
                kind === 'pdf' ? stockReorderApi.exportPdf : stockReorderApi.exportExcel,
                singleOpts
            );
        },
        [sheet, singleOpts, storedSheetId, runExport]
    );

    const exportSupplier = useCallback(
        (supplier) => {
            if (singleOpts) {
                return runExport(stockReorderApi.exportExcel, singleOpts, supplier);
            }
            if (storedSheetId) {
                return runExport(stockReorderApi.exportSnapshot, storedSheetId, 'xlsx', supplier);
            }
        },
        [singleOpts, storedSheetId, runExport]
    );

    const orderCsv = useCallback(
        (supplier) => {
            if (singleOpts) {
                return runExport(stockReorderApi.exportSupplierOrder, singleOpts, supplier);
            }
            if (storedSheetId) {
                return runExport(stockReorderApi.exportSnapshot, storedSheetId, 'csv', supplier);
            }
        },
        [singleOpts, storedSheetId, runExport]
    );

    const tabs = [
        { id: 'reorder', label: 'Reorder Sheet' },
        { id: 'stock', label: 'Stock Availability' },
        { id: 'history', label: 'Saved Sheets' },
        { id: 'stock-uploads', label: 'Stock Uploads' },
        ...(isAdmin
            ? [
                  { id: 'mapping', label: 'Supplier Mapping' },
                  { id: 'rules', label: 'Supplier Rules' },
                  { id: 'branches', label: 'Companies & Branches' },
                  { id: 'access', label: 'Branch Access' }
              ]
            : [])
    ];

    return (
        <div className="stock-reorder-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="stock-reorder-content">
                <div className="page-header">
                    <img src="/logo.png" alt="Reception Logo" className="app-logo" />
                    <div className="page-header-text">
                        <h1>Stock Reorder</h1>
                        <p>Reorder points &amp; quantities from stock movement</p>
                    </div>
                    <div className="page-header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                            <span className="user-role">
                                {isAdmin ? 'Module Admin' : user?.role}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn-back"
                            onClick={() => navigate('/purpose-selection')}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                <div className="sr-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`sr-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="sr-body">
                    {loading && <div className="sr-empty">Loading…</div>}

                    {!loading && error && (
                        <div className="sr-alert sr-alert-error">
                            <strong>{error}</strong>
                            <p>
                                The stock reorder service runs separately from reception. If this
                                persists, check that it is running and reachable.
                            </p>
                            <button className="sr-btn" onClick={load}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {/* A user with the card but no branches assigned would otherwise
                                just see empty dropdowns with no explanation. */}
                            {!isAdmin && businessUnits.length === 0 && (
                                <div className="sr-alert sr-alert-warn">
                                    <strong>No branches assigned yet</strong>
                                    <p>
                                        You have access to this module, but no branches have been
                                        assigned to you. Ask a stock reorder administrator to grant
                                        them under <em>Branch Access</em>.
                                    </p>
                                </div>
                            )}

                            {actionError && (
                                <div className="sr-alert sr-alert-error">{actionError}</div>
                            )}

                            {/* Offered until it is restored, kept, or deleted —
                                a generated order is expensive to reproduce. */}
                            {draft && !sheet && (
                                <div className="sr-draft-banner">
                                    <div className="sr-draft-text">
                                        <strong>Your generated order is saved as a draft</strong>
                                        <span>
                                            {draft.is_combined
                                                ? `${draft.branches.length} branches combined`
                                                : draft.branch || 'this branch'}{' '}
                                            · {new Date(draft.created_at).toLocaleString()} ·{' '}
                                            {Number(draft.summary?.total_items || 0).toLocaleString()}{' '}
                                            items,{' '}
                                            {Number(
                                                draft.summary?.items_needing_reorder || 0
                                            ).toLocaleString()}{' '}
                                            need reorder
                                        </span>
                                    </div>
                                    <div className="sr-draft-actions">
                                        <button
                                            className="sr-btn sr-btn-primary sr-btn-sm"
                                            onClick={restoreDraft}
                                            disabled={draftBusy}
                                        >
                                            {draftBusy ? 'Opening…' : 'View draft'}
                                        </button>
                                        <button
                                            className="sr-btn sr-btn-sm"
                                            onClick={keepDraft}
                                            disabled={draftBusy}
                                            title="Keep it permanently under Saved Sheets"
                                        >
                                            Save permanently
                                        </button>
                                        <button
                                            className="sr-btn sr-btn-sm sr-btn-danger"
                                            onClick={discardDraft}
                                            disabled={draftBusy}
                                        >
                                            Delete draft
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'reorder' && (
                                <ReorderTab
                                    businessUnits={businessUnits}
                                    isAdmin={isAdmin}
                                    branchId={branchId}
                                    onBranchChange={changeBranch}
                                    sheet={sheet}
                                    snapshot={snapshot}
                                    hasLive={Boolean(liveSheet)}
                                    editable={editable}
                                    busyPart={busyPart}
                                    onSheet={applySheet}
                                    onBackToLive={backToLive}
                                    onEdit={editItem}
                                    onReset={resetItem}
                                    onExportSheet={exportSheet}
                                    onExportSupplier={exportSupplier}
                                    onOrderCsv={orderCsv}
                                    canExport={canExport}
                                    onToast={showToast}
                                />
                            )}
                            {activeTab === 'stock' && (
                                <StockAvailabilityTab
                                    businessUnits={businessUnits}
                                    branchId={branchId}
                                    onBranchChange={changeBranch}
                                    canUpload={canUploadStock}
                                    historyUpload={stockHistory}
                                    onBackToCurrent={() => setStockHistory(null)}
                                    onToast={showToast}
                                />
                            )}
                            {activeTab === 'stock-uploads' && (
                                <StockUploadsTab
                                    businessUnits={businessUnits}
                                    branchId={branchId}
                                    onBranchChange={changeBranch}
                                    isAdmin={isAdmin}
                                    onView={(upload) => {
                                        setStockHistory(upload.status === 'active' ? null : upload);
                                        setActiveTab('stock');
                                    }}
                                    onToast={showToast}
                                />
                            )}
                            {activeTab === 'history' && (
                                <HistoryTab
                                    branchId={branchId}
                                    businessUnits={businessUnits}
                                    onBranchChange={changeBranch}
                                    onOpenSnapshot={openSnapshot}
                                    onToast={showToast}
                                    viewingId={snapshot?.id}
                                />
                            )}
                            {activeTab === 'mapping' && isAdmin && (
                                <SupplierMappingTab onToast={showToast} />
                            )}
                            {activeTab === 'rules' && isAdmin && (
                                <SupplierRulesTab onToast={showToast} />
                            )}
                            {activeTab === 'branches' && isAdmin && (
                                <BranchesTab onChanged={loadBusinessUnits} />
                            )}
                            {activeTab === 'access' && isAdmin && <AccessTab />}
                        </>
                    )}
                </div>
            </div>

            {toasts.length > 0 && (
                <div className="sr-toasts">
                    {toasts.map((t) => (
                        <div className="sr-toast" key={t.id}>
                            {t.message}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StockReorder;
